import {
  useQuery,
  useInfiniteQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult
} from "react-query";
import {
  type GetAllKindsArgs,
  getAllKinds,
  getWorkflowKindsDynamicAPI,
  getWorkflowKindsDynamicAPIUnlimited,
  getPaginatedWorkflowKindsDynamicAPI
} from "../services/kinds.services";
import { type Kind, PermissionTypes } from "@certa/types";

import { useCheckPermission } from "@certa/common/utils/permissions/Chowkidar";
import type { UseQueryReturnType } from "@certa/common/types";
import { paginatedParams } from "../utils/utils";
import { useEnableDataObjects } from "@certa/common/hooks/useEnableDataObjects";

// Default pagination parameters
const DEFAULT_LIMIT = 20;

export const getKindsQuery = (params?: GetAllKindsArgs, useV3?: boolean) => {
  return {
    queryKey: ["kinds", params, useV3] as QueryKey,
    queryFn: () => getAllKinds({ ...params, useV3 }),
    // 5 hrs
    staleTime: 1000 * 60 * 60 * 5
  };
};

export const useKinds = (
  params?: GetAllKindsArgs,
  config?: UseQueryOptions<UseQueryReturnType<typeof getAllKinds>>
): UseQueryResult<Kind[], unknown> => {
  const shouldUseV3 = useEnableDataObjects();

  return useQuery({
    ...getKindsQuery(params, shouldUseV3),
    ...config
  });
};

export const useKindId = (kindTag: string) => {
  const { data: kinds } = useKinds();
  return kinds?.find(kind => kind.tag === kindTag)?.id;
};

export const useKind = (kindTag: string) => {
  const { data: kinds } = useKinds();
  return kinds?.find(kind => kind.tag === kindTag);
};

export const useKindFromId = (kindId: number) => {
  const { data: kinds } = useKinds();
  return kinds?.find(kind => kind.id === kindId);
};

export const useCreatableWorkflowKind = (
  kindTag: string,
  disableIsRelatedKindCheck?: boolean
) => {
  const { data: kinds } = useKinds();
  const canCreateWorkflows = useCheckPermission(
    PermissionTypes.CAN_ADD_WORKFLOW
  );
  return canCreateWorkflows
    ? kinds?.find(
        kind =>
          kindTag === kind.tag &&
          (disableIsRelatedKindCheck || kind.isRelatedKind) &&
          kind.features.includes("add_workflow")
      )
    : undefined;
};

type UseGetWorkflowKindsDynamicAPIParams = {
  filters: {
    id__in?: number[];
    tag__in?: string[];
    questionnaire_kinds_only?: boolean;
  };
};

const transformWorkflowKindsParams = (filters?: {
  id__in?: number[];
  tag__in?: string[];
  questionnaire_kinds_only?: boolean;
}) => ({
  data: {
    id: null,
    name: null,
    tag: null,
    related_kind_tags: null,
    dynamic_group_names: null
  },
  filters: {
    id__in: filters?.id__in?.join(","),
    tag__in: filters?.tag__in?.join(","),
    questionnaire_kinds_only: filters?.questionnaire_kinds_only
  }
});

export const useGetWorkflowKindsDynamicAPI = (
  params: UseGetWorkflowKindsDynamicAPIParams,
  config?: UseQueryOptions<
    UseQueryReturnType<typeof getWorkflowKindsDynamicAPI>
  >
) => {
  const { filters } = params;
  const transformedParams = transformWorkflowKindsParams(filters);

  return useQuery({
    queryKey: ["workflow-kinds-dynamic-api", filters],
    queryFn: () => getWorkflowKindsDynamicAPI(transformedParams),
    ...config
  });
};

export const useGetWorkflowKindsDynamicAPIUnlimited = (
  params: UseGetWorkflowKindsDynamicAPIParams,
  config?: UseQueryOptions<
    UseQueryReturnType<typeof getWorkflowKindsDynamicAPIUnlimited>
  >
) => {
  const { filters } = params;
  const transformedParams = transformWorkflowKindsParams(filters);

  return useQuery({
    queryKey: ["workflow-kinds-dynamic-api-unlimited", filters],
    queryFn: () => getWorkflowKindsDynamicAPIUnlimited(transformedParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    ...config
  });
};

type UseGetPaginatedWorkflowKindsDynamicAPIParams = {
  filters?: {
    id__in?: number[];
    tag__in?: string[];
  };
  limit?: number;
  config?: any;
};

type PaginatedWorkflowKindsResponse = {
  results: Kind[];
  count: number;
  next: string | null;
  previous: string | null;
};

export const useGetPaginatedWorkflowKindsDynamicAPI = (
  props: UseGetPaginatedWorkflowKindsDynamicAPIParams = {}
) => {
  const { filters, limit = DEFAULT_LIMIT, config } = props;

  const transformedParams = {
    data: {
      id: null,
      name: null,
      tag: null,
      related_kind_tags: null,
      dynamic_group_names: null
    },
    filters: {
      id__in: filters?.id__in?.join(","),
      tag__in: filters?.tag__in?.join(",")
    }
  };

  return useInfiniteQuery<PaginatedWorkflowKindsResponse>({
    ...paginatedParams,
    ...config,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryKey: ["workflow-kinds-dynamic-api-paginated", filters],
    queryFn: ({ pageParam = 0 }) => {
      return getPaginatedWorkflowKindsDynamicAPI({
        payload: transformedParams,
        offset: pageParam,
        limit
      });
    }
  });
};
